import { useQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { GET_STANDARD_LABELS } from '../graphql/standardLabels';

export interface StandardLabel {
  id: string;
  name: string;
  value: string;
}

interface StandardLabelsResponse {
  standardLabels: {
    labels: Array<{
      id: string;
      labelName: string;
      labelShortName: string;
      policy: string;
      scope: {
        global: boolean;
        communityGroups: string[];
        communities: string[];
      };
      labelStatus: string;
      labelNumMap: number;
      createdBy: string;
      createdAt: string;
      lastModifiedBy: string;
      lastModifiedAt: string;
      backupVersions: string[];
    }>;
  };
}

/**
 * Hook to get standard labels for content filtering from the Labels service
 */
export function useStandardLabels() {
  const { data, loading, error, refetch } = useQuery<StandardLabelsResponse>(GET_STANDARD_LABELS, {
    fetchPolicy: 'cache-first',
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const standardLabels: StandardLabel[] = useMemo(() => {
    const serviceLabels = data?.standardLabels?.labels || [];

    // Map service labels
    const mappedLabels = serviceLabels.map(label => ({
      id: label.id,
      name: label.labelName,
      value: label.labelShortName || label.labelName.toLowerCase().replace(/\s+/g, '_'),
    }));

    // Add the "Safe" label that isn't returned from the service
    const safeLabel: StandardLabel = {
      id: 'safe',
      name: 'Safe',
      value: 'safe',
    };

    return [safeLabel, ...mappedLabels];
  }, [data]);

  return {
    standardLabels,
    loading,
    errors,
    refetch,
  };
}

export default useStandardLabels;
